import { render } from "preact";
import { html } from "htm/preact";
import { currentPath, pathSegments } from "./router.js";
import { AppShell, type Tab } from "./components/AppShell.js";
import { Home } from "./screens/Home.js";
import { Join } from "./screens/Join.js";
import { CreateTournament } from "./screens/CreateTournament.js";
import { CourseLibrary } from "./screens/CourseLibrary.js";
import { Setup } from "./screens/Setup.js";
import { Scorecard } from "./screens/Scorecard.js";
import { RoundRules } from "./screens/RoundRules.js";
import { Leaderboard } from "./screens/Leaderboard.js";
import { Admin } from "./screens/Admin.js";
import { AdminDebug } from "./screens/AdminDebug.js";
import { OrganiserRecovery } from "./screens/OrganiserRecovery.js";
import { RestoreAccess } from "./screens/RestoreAccess.js";
import { PlayerRound } from "./screens/PlayerRound.js";
import { PlayerTotal } from "./screens/PlayerTotal.js";
import { RoundBreakdown } from "./screens/RoundBreakdown.js";
import { Clubhouse } from "./screens/Clubhouse.js";
import { watchSystemTheme } from "./theme.js";
import { prefersReducedMotion } from "./animation.js";
import { getIdentity } from "./identity.js";
import { TERMS } from "./copy.js";

function routeScreen(parts: string[]): {
  screen: unknown;
  code: string | null;
  tab: Tab;
  bare?: boolean;
} {
  if (parts[0] === "join") {
    return { screen: html`<${Join} code=${parts[1] ?? ""} />`, code: null, tab: null };
  }
  if (parts[0] === "create") return { screen: html`<${CreateTournament} />`, code: null, tab: null };
  if (parts[0] === "admin" && parts[1] === "debug") {
    return { screen: html`<${AdminDebug} />`, code: null, tab: null };
  }
  if (parts[0] === "admin") return { screen: html`<${Admin} />`, code: null, tab: null };
  // The way back in for a phone whose home-screen app a tapped link can never reach.
  if (parts[0] === "restore") return { screen: html`<${RestoreAccess} />`, code: null, tab: null };

  if (parts[0] === "library") return { screen: html`<${CourseLibrary} />`, code: null, tab: null };

  if (parts[0] === "t" && parts[1]) {
    const code = parts[1];
    // New links say "organizer"; the older British spelling stays routable so a
    // recovery link already texted to someone doesn't become a dead end.
    if ((parts[2] === "organizer" || parts[2] === "organiser") && parts[3]) {
      return {
        screen: html`<${OrganiserRecovery} code=${code} token=${parts[3]} />`,
        code: null,
        tab: null,
      };
    }
    if (parts[2] === "clubhouse") {
      // Watching only. The screen strips the app bar and the tab bar to be readable
      // from across a room, and taking those away from someone who came here to score
      // would strand them — so the guard lives out here, where `bare` is decided,
      // rather than inside a screen that has already thrown the way out away.
      if (getIdentity(code)?.role !== "spectator") {
        return {
          screen: html`<${Leaderboard}
            code=${code}
            notice="The clubhouse screen is part of watching a ${TERMS.event}. Join with the code as a spectator on the device you want to put it up on."
          />`,
          code,
          tab: "leaderboard",
        };
      }
      return { screen: html`<${Clubhouse} code=${code} />`, code, tab: "clubhouse", bare: true };
    }
    if (parts[2] === "setup") return { screen: html`<${Setup} code=${code} />`, code, tab: "setup" };
    if (parts[2] === "leaderboard") {
      return { screen: html`<${Leaderboard} code=${code} />`, code, tab: "leaderboard" };
    }
    // Score breakdowns. Routes rather than modals so the back button works and the
    // list they were opened from doesn't have to change shape.
    if (parts[2] === "player" && parts[3]) {
      return {
        screen: html`<${PlayerTotal} code=${code} playerId=${parts[3]} />`,
        code,
        tab: "leaderboard",
      };
    }
    if (parts[2] === "round" && parts[3] && parts[4] === "player" && parts[5]) {
      return {
        screen: html`<${PlayerRound} code=${code} roundId=${parts[3]} playerId=${parts[5]} />`,
        code,
        tab: "leaderboard",
      };
    }
    if (parts[2] === "round" && parts[3] && parts[4] === "holes") {
      return {
        screen: html`<${RoundBreakdown} code=${code} roundId=${parts[3]} />`,
        code,
        tab: "leaderboard",
      };
    }
    if (parts[2] === "round" && parts[3] && parts[4] === "rules") {
      return {
        screen: html`<${RoundRules} code=${code} roundId=${parts[3]} />`,
        code,
        tab: "scorecard",
      };
    }
    if (parts[2] === "round" && parts[3]) {
      return {
        screen: html`<${Scorecard} code=${code} roundId=${parts[3]} />`,
        code,
        tab: "scorecard",
      };
    }
  }

  return { screen: html`<${Home} />`, code: null, tab: null };
}

function App() {
  const { screen, code, tab, bare } = routeScreen(pathSegments(currentPath.value));
  return html`<${AppShell} code=${code} activeTab=${tab} bare=${bare ?? false}>${screen}<//>`;
}

/** How long the launch strike runs, matching the 62.5% mark of the 1.6s `mark-*`
 * keyframes in styles.css. Change one, change both. */
const SPLASH_MS = 1000;
/** Matches the opacity transition on `.splash`; the node is removed on transitionend,
 * this is only the backstop for when that event doesn't arrive. */
const SPLASH_FADE_MS = 260;

/**
 * Takes the launch screen down once its strike has played.
 *
 * The wait is measured off the animation's own clock rather than `performance.now()`,
 * so a slow load that has already played a full strike fades out at once instead of
 * sitting through another one — the screen is up for the strike, never for longer than
 * the app needs.
 */
function dismissSplash() {
  const splash = document.getElementById("splash");
  if (!splash) return;

  const done = () => {
    splash.classList.add("is-done");
    // Belt and braces: transitionend doesn't fire if the element is display:none'd or
    // the transition is collapsed, and a full-screen layer that never leaves the DOM
    // would swallow every tap underneath it.
    const drop = () => splash.remove();
    splash.addEventListener("transitionend", drop, { once: true });
    setTimeout(drop, SPLASH_FADE_MS + 100);
  };

  // Not just a courtesy: the global prefers-reduced-motion block in styles.css collapses
  // the strike to a 0.01ms duration, so its currentTime caps near zero and the
  // arithmetic below would compute a *full* SPLASH_MS wait — exactly backwards. Reduced
  // motion has to skip the hold, not extend it.
  if (prefersReducedMotion()) {
    done();
    return;
  }

  const [strike] = splash.getAnimations?.({ subtree: true }) ?? [];
  const played = Number(strike?.currentTime ?? SPLASH_MS);
  setTimeout(done, Math.max(0, SPLASH_MS - (Number.isFinite(played) ? played : SPLASH_MS)));
}

watchSystemTheme();

const root = document.getElementById("app");
if (root) {
  // Preact's render() doesn't clear pre-existing non-Preact DOM content on its own.
  // The launch screen lives outside #app now, so there's usually nothing here to
  // clear — but sw.ts caches /index.html, so a client can still be holding a shell
  // from before the move, where the splash *was* inside #app. This is the guard for
  // that one.
  root.textContent = "";
  render(html`<${App} />`, root);
}

dismissSplash();

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      // offline app shell just won't be pre-cached this session
    });
  });
}
